import { Link } from 'react-router-dom';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-4 leading-snug">
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg md:text-xl font-bold text-gray-800 mt-8 mb-3">
      {children}
    </h3>
  );
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-gray-600 text-base leading-relaxed mb-4">
      {children}
    </p>
  );
}

function TipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-teal-50 border-l-4 border-teal-400 rounded-r-xl px-6 py-5 my-6">
      <div className="flex items-start gap-3">
        <i className="ri-lightbulb-line text-teal-500 text-xl mt-0.5 shrink-0"></i>
        <p className="text-teal-800 text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function WarningBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl px-6 py-5 my-6">
      <div className="flex items-start gap-3">
        <i className="ri-error-warning-line text-amber-500 text-xl mt-0.5 shrink-0"></i>
        <p className="text-amber-800 text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

interface ConsiderationCardProps {
  icon: string;
  number: string;
  title: string;
  description: string;
}

function ConsiderationCard({ icon, number, title, description }: ConsiderationCardProps) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 flex gap-4 items-start hover:-translate-y-0.5 transition-transform duration-200">
      <div className="shrink-0 flex flex-col items-center gap-2">
        <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-xl">
          <i className={`${icon} text-teal-500 text-xl`}></i>
        </div>
        <span className="text-xs font-bold text-teal-400">{number}</span>
      </div>
      <div>
        <h4 className="font-bold text-gray-900 text-sm mb-1">{title}</h4>
        <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

const considerations = [
  {
    icon: 'ri-ruler-2-line',
    number: '01',
    title: 'Get the Size Right First',
    description: 'Before anything else, nail down your guest count and event layout. Seated dining needs roughly 1.8–2.2 m² per person; standing cocktail events around 0.7–1 m². Always build in a 15–20% buffer for staging, bars, and walkways.',
  },
  {
    icon: 'ri-calendar-check-line',
    number: '02',
    title: 'Book Well in Advance',
    description: 'Peak season (October–April for warmer climates) sees high demand. Booking 8–12 weeks ahead gives you more tent size and colour options and ensures the supplier can plan logistics around your venue.',
  },
  {
    icon: 'ri-map-pin-2-line',
    number: '03',
    title: 'Site Survey Is Non-Negotiable',
    description: 'Photos and Google Maps are a starting point, but a proper on-site assessment by your supplier is essential. Slopes, overhead cables, underground pipes, and access road widths all affect installation feasibility.',
  },
  {
    icon: 'ri-anchor-line',
    number: '04',
    title: 'Understand Your Anchoring Options',
    description: 'On grass or soft ground, steel stakes are standard. On concrete, paving, or wooden decking, weighted ballast systems or structural fixing brackets are used. Confirm surface type with your supplier early, as anchoring affects the whole setup plan.',
  },
  {
    icon: 'ri-cloud-windy-line',
    number: '05',
    title: 'Weather & Wind Load Ratings',
    description: 'Stretch tents are engineered to flex in wind rather than resist it, but they do have limits. Ask your supplier for the wind load rating of your specific tent and ensure the anchoring plan matches the expected weather conditions.',
  },
  {
    icon: 'ri-file-text-line',
    number: '06',
    title: 'Permits & Local Regulations',
    description: 'Many councils and venues require a temporary structure permit for tents above a certain size. In some areas, a structural engineer\'s sign-off is needed. Your supplier should guide you through this, but ultimately the responsibility rests with the event organiser.',
  },
  {
    icon: 'ri-plug-line',
    number: '07',
    title: 'Power & Lighting Integration',
    description: 'Stretch tents don\'t come with built-in wiring. Plan your lighting rig, generator placement, and cable routing before the install day. Festoon lights, LED uplights, and suspended chandeliers all require rigging points inside the tent structure.',
  },
  {
    icon: 'ri-truck-line',
    number: '08',
    title: 'Delivery, Setup & Breakdown Times',
    description: 'A large stretch tent can take 4–8 hours to install and 2–4 hours to break down. Factor this into your venue hire window. Confirm with your supplier exactly when they need access, as arriving to find crew still setting up is not a good start.',
  },
];

interface ChecklistItemProps {
  text: string;
}

function ChecklistItem({ text }: ChecklistItemProps) {
  return (
    <li className="flex items-start gap-3 text-gray-600 text-sm leading-relaxed">
      <span className="w-5 h-5 flex items-center justify-center bg-teal-100 rounded-full shrink-0 mt-0.5">
        <i className="ri-check-line text-teal-600 text-xs"></i>
      </span>
      {text}
    </li>
  );
}

const supplierQuestions = [
  'What is the wind load rating for this tent size?',
  'Do you carry public liability insurance?',
  'Who is responsible for obtaining permits?',
  'What anchoring method will you use on my surface type?',
  'How many crew members will be on site for installation?',
  'What happens if the tent is damaged during the event?',
  'Is there a wet weather or cancellation policy?',
  'Can the tent feature a custom printed skin with our logo?',
  'Do you offer after-hours or weekend installation?',
  'What is included in the rental price vs. charged extra?',
];

const rentalIncludes = [
  { label: 'Tent fabric and poles', included: true },
  { label: 'Standard anchoring hardware', included: true },
  { label: 'Installation and breakdown labour', included: true },
  { label: 'Transport to/from venue', included: true },
  { label: 'Lighting (festoon, uplights, chandeliers)', included: false },
  { label: 'Flooring or carpeting', included: false },
  { label: 'Sidewall lining or draping', included: false },
  { label: 'Generator or power supply', included: false },
  { label: 'Permit application fees', included: false },
  { label: 'Furniture or décor', included: false },
];

export default function ArticleBody() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      {/* Intro */}
      <Paragraph>
        Renting a stretch tent for the first time can feel overwhelming. There are sizes, anchoring systems, permits, weather ratings, and supplier terms to navigate before you even get to the fun parts. This guide cuts through the noise and gives you the practical knowledge you need to rent with confidence and avoid the most common pitfalls.
      </Paragraph>

      {/* Hero image */}
      <div className="rounded-2xl overflow-hidden my-8 w-full h-72 md:h-96 bg-gray-100">
        <img
          src="/assets/img/ddf123f33140.webp"
          alt="Professional stretch tent installation crew at an outdoor event"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Why stretch tents */}
      <SectionHeading>Why Stretch Tents Over Traditional Marquees?</SectionHeading>
      <Paragraph>
        Before diving into the rental process, it helps to understand what makes stretch tents unique. Unlike traditional frame marquees or pole tents, stretch tents are made from high-tensile PVC fabric that flexes and contours to the landscape. They can be pitched on uneven ground, around trees, against buildings, and in irregular shapes that rigid structures simply can't accommodate.
      </Paragraph>
      <Paragraph>
        They're also significantly faster to install and break down, which matters for venues with tight hire windows. And aesthetically, the organic curves and variety of colour options give stretch tents a look that's become synonymous with premium outdoor events.
      </Paragraph>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
        {[
          { icon: 'ri-time-line', label: 'Fast Setup', detail: '4–8 hrs for large tents' },
          { icon: 'ri-palette-line', label: 'Full Colour Range', detail: 'White, natural, charcoal + more' },
          { icon: 'ri-landscape-line', label: 'Any Terrain', detail: 'Grass, concrete, slopes, decking' },
        ].map((item) => (
          <div key={item.label} className="bg-gray-50 rounded-2xl p-6 text-center">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl mx-auto mb-3">
              <i className={`${item.icon} text-teal-500 text-lg`}></i>
            </div>
            <p className="font-bold text-gray-900 text-sm mb-1">{item.label}</p>
            <p className="text-gray-500 text-xs">{item.detail}</p>
          </div>
        ))}
      </div>

      {/* 8 key considerations */}
      <SectionHeading>8 Key Considerations Before You Rent</SectionHeading>
      <Paragraph>
        These are the factors our team walks through with every new client. Go through each one carefully before signing a rental agreement.
      </Paragraph>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {considerations.map((c) => (
          <ConsiderationCard key={c.number} {...c} />
        ))}
      </div>

      <TipBox>
        Don't wait until a week before your event to start the conversation with your supplier. The best rental companies are booked out 2–3 months ahead during peak season, and a rushed quote often means a compromised setup plan.
      </TipBox>

      {/* What's typically included */}
      <SectionHeading>What&apos;s Typically Included in a Rental?</SectionHeading>
      <Paragraph>
        Rental packages vary between suppliers. Here&apos;s a general breakdown of what&apos;s usually covered, and what you&apos;ll typically need to source separately or add as extras:
      </Paragraph>
      <div className="bg-gray-50 rounded-2xl overflow-hidden mt-6 border border-gray-100">
        <div className="grid grid-cols-[1fr_auto] text-xs font-bold text-gray-500 uppercase tracking-wide px-6 py-3 border-b border-gray-200">
          <span>Item</span>
          <span>Usually Included?</span>
        </div>
        {rentalIncludes.map((row) => (
          <div
            key={row.label}
            className="grid grid-cols-[1fr_auto] items-center px-6 py-4 border-b border-gray-100 last:border-0"
          >
            <span className="text-gray-700 text-sm">{row.label}</span>
            {row.included ? (
              <span className="flex items-center gap-1.5 text-teal-600 text-xs font-semibold">
                <i className="ri-checkbox-circle-line text-base"></i> Yes
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-gray-400 text-xs font-semibold">
                <i className="ri-close-circle-line text-base"></i> Extra
              </span>
            )}
          </div>
        ))}
      </div>

      <WarningBox>
        Always request a fully itemised quote that specifies exactly what is and isn&apos;t included. Hidden extras, like after-hours installation fees, fuel surcharges, or late-return penalties, can add significantly to the total cost.
      </WarningBox>

      {/* Anchoring deep dive */}
      <SectionHeading>A Closer Look at Anchoring Systems</SectionHeading>
      <Paragraph>
        Anchoring is the most technically important part of a stretch tent installation. Get it wrong and you have a safety hazard; get it right and the tent performs flawlessly in all conditions.
      </Paragraph>

      <SubHeading>Grass &amp; Soft Ground</SubHeading>
      <Paragraph>
        The most straightforward surface to work with. Heavy-duty steel ground stakes (typically 600–900mm long) are driven at intervals around the tent perimeter. In very soft or waterlogged ground, longer stakes or corkscrew anchors may be needed.
      </Paragraph>

      <SubHeading>Concrete &amp; Hard Surfaces</SubHeading>
      <Paragraph>
        On hard surfaces, weight bags filled with water or sand are placed around the perimeter and connected to the tent edges via tensioning straps. Alternatively, concrete anchors can be drilled and fitted, but this is usually only done for semi-permanent or long-term installations.
      </Paragraph>

      <SubHeading>Mixed Terrain &amp; Slopes</SubHeading>
      <Paragraph>
        Sloped or mixed-surface venues require a hybrid approach. Your supplier should assess the site before the installation date and prepare the appropriate anchoring kit. Never assume a standard setup will work, as slopes can significantly increase wind load on one side of the tent.
      </Paragraph>

      {/* Image 2 */}
      <div className="rounded-2xl overflow-hidden my-8 w-full h-64 md:h-80 bg-gray-100">
        <img
          src="/assets/img/aa0cf953b11b.webp"
          alt="Stretch tent anchoring stakes being installed on grass at an outdoor event"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Permits section */}
      <SectionHeading>Navigating Permits &amp; Council Approvals</SectionHeading>
      <Paragraph>
        This is the area most first-time renters overlook, and it&apos;s one that can cause real headaches if left too late. Permit requirements vary by location, venue type, and tent size. Here&apos;s what to be aware of:
      </Paragraph>
      <ul className="space-y-3 my-5 ml-1">
        {[
          'Most councils require a permit for temporary structures above a certain footprint (typically 20–50 m²).',
          'Some venues have blanket approval for temporary structures. Always ask your venue coordinator first.',
          'Events in national parks, on public land, or near heritage-listed buildings often require additional approvals.',
          'A structural engineer\'s certification may be required for tents at ticketed public events.',
          'Fire safety regulations may require a minimum distance from buildings and clearly marked exits.',
          'Allow at least 4–6 weeks for permit applications, as some councils are slower than others.',
        ].map((item) => (
          <ChecklistItem key={item} text={item} />
        ))}
      </ul>

      <TipBox>
        Ask your rental supplier if they have experience with your specific council or venue. A good supplier will have navigated these approvals many times before and can often advise on the fastest path to approval.
      </TipBox>

      {/* Questions to ask supplier */}
      <SectionHeading>10 Questions to Ask Your Supplier Before Signing</SectionHeading>
      <Paragraph>
        A professional rental company should be able to answer all of these confidently. If they can&apos;t, consider it a red flag.
      </Paragraph>
      <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {supplierQuestions.map((q, i) => (
            <div key={q} className="flex items-start gap-3">
              <span className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-full text-teal-600 text-xs font-bold shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span className="text-gray-700 text-sm leading-relaxed">{q}</span>
            </div>
          ))}
        </div>
      </div>

      {/* On the day */}
      <SectionHeading>On the Day: What to Expect</SectionHeading>
      <Paragraph>
        A smooth installation day requires coordination between you, your venue, and your supplier. Here&apos;s a typical timeline for a medium-to-large tent installation:
      </Paragraph>
      <div className="space-y-3 mt-5">
        {[
          { time: 'Day Before', label: 'Site access confirmed, anchoring kit delivered if required' },
          { time: 'Early Morning', label: 'Crew arrives, perimeter marked, anchoring begins' },
          { time: 'Mid Morning', label: 'Fabric rolled out, poles positioned, initial tensioning' },
          { time: 'Late Morning', label: 'Final tensioning, edge finishing, rigging point installation' },
          { time: 'Midday', label: 'Lighting, power, and décor teams move in' },
          { time: 'Afternoon', label: 'Final walkthrough with event organiser, handover' },
        ].map((item) => (
          <div key={item.time} className="flex items-start gap-4 bg-white border border-gray-100 rounded-xl px-5 py-4">
            <span className="text-teal-500 font-bold text-xs whitespace-nowrap w-28 shrink-0 mt-0.5">{item.time}</span>
            <span className="text-gray-600 text-sm leading-relaxed">{item.label}</span>
          </div>
        ))}
      </div>

      {/* After the event */}
      <SectionHeading>After the Event: Breakdown &amp; Return</SectionHeading>
      <Paragraph>
        Most rental agreements require the venue to be cleared and the tent removed within 24–48 hours of the event ending. Plan your post-event schedule around this window, as caterers, lighting companies, and flooring crews all need to clear out before the tent can come down.
      </Paragraph>
      <Paragraph>
        Do a walk-through with the installation crew before they leave to document the condition of the tent and any site markings. This protects both you and the supplier from disputes over damage or missing equipment.
      </Paragraph>

      {/* Final thoughts */}
      <SectionHeading>Final Thoughts</SectionHeading>
      <Paragraph>
        Renting a stretch tent is one of the best decisions you can make for an outdoor event. The right tent transforms a field into an experience. But like all event infrastructure, it rewards preparation. Get your sizing right, choose a reputable supplier, sort permits early, and communicate clearly about access and logistics. Do those things, and the rest flows naturally.
      </Paragraph>
      <Paragraph>
        If you&apos;re ready to start planning, browse our range of stretch tent options or reach out for a free consultation and site assessment.
      </Paragraph>

      {/* Internal links */}
      <div className="flex flex-wrap gap-3 mt-6">
        <Link
          to="/products/standard-stretch-tents"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-tent-line"></i>
          View Standard Tents
        </Link>
        <Link
          to="/blog/suitable-stretch-tent-sizes-for-your-outdoor-event"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-ruler-2-line"></i>
          Tent Sizing Guide
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-mail-line"></i>
          Request a Quote
        </Link>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
        <span className="text-xs text-gray-400 mr-1 self-center">Tags:</span>
        {['Stretch Tents', 'Tent Rental', 'Event Planning', 'Outdoor Events', 'Event Tips', 'Anchoring'].map((tag) => (
          <span
            key={tag}
            className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}
